import React, { useEffect, useState } from 'react';
import { useWebRTCContext } from '../WebRTCContext';
import * as S from '../styled';

const AudioVisualizer = () => {
  const { state } = useWebRTCContext();
  const { audioLevels, connectionState } = state;
  
  // // Log received state for debugging
  // console.log('[AudioVisualizer] connectionState:', connectionState);
  // console.log('[AudioVisualizer] audioLevels:', audioLevels);
  
  const barCount = 18;
  const animationInterval = 80; // ms, for smoother animation

  const [userBars, setUserBars] = useState(Array(barCount).fill(0));
  const [aiBars, setAiBars] = useState(Array(barCount).fill(0));

  useEffect(() => {
    // // Log inside useEffect to see values when effect runs
    // console.log('[AudioVisualizer useEffect] connectionState:', connectionState);
    // console.log('[AudioVisualizer useEffect] audioLevels:', audioLevels);

    if (connectionState !== 'connected') {
      // console.log('[AudioVisualizer useEffect] Not connected, setting bars to 0.');
      setUserBars(Array(barCount).fill(0));
      setAiBars(Array(barCount).fill(0));
      return () => {}; // Clear any potential lingering intervals if any
    }

    const createWaveform = (level) => {
      // // Log level received by createWaveform
      // // console.log('[AudioVisualizer createWaveform] level:', level);
      // // Disabled for now to prevent excessive logging from setInterval

      if (level <= 1) { // Threshold for silence or very low sound
        // Gentle ambient movement for near silence
        return Array(barCount).fill(0).map(() => Math.random() * 2 + 1); // Small random heights between 1 and 3
      }

      const basePattern = Array(barCount).fill(0).map((_, i) => {
        const position = i / (barCount - 1); // 0 to 1
        // Sine waves for a more organic feel
        const sinValuePrimary = Math.sin(position * Math.PI * 2 + Date.now() / 200); // Main wave, animates over time
        const sinValueSecondary = Math.sin(position * Math.PI * 3 + Date.now() / 300 + 0.5); // Secondary wave
        // Combine and normalize
        let combined = (sinValuePrimary + sinValueSecondary) / 2; // Range from -1 to 1
        combined = (combined + 1) / 2; // Normalize to 0 to 1
        return combined;
      });

      return basePattern.map(baseHeight => {
        // Scale height by audio level (0-100)
        // Max height of a bar is 100 (as percentage for styled component)
        const heightPercent = baseHeight * level;
        // Add slight random variation to make it less uniform
        const randomFactor = (Math.random() * 0.4 - 0.2); // Adds/subtracts up to 20% of level
        let finalHeight = heightPercent + heightPercent * randomFactor;
        finalHeight = Math.max(0, Math.min(finalHeight, 100)); // Clamp between 0 and 100
        return finalHeight;
      });
    };

    const updateUserBars = () => {
      const userLevel = audioLevels.user || 0;
      // // console.log('[AudioVisualizer updateUserBars] userLevel:', userLevel); // Disabled, too noisy
      setUserBars(createWaveform(userLevel));
    };

    const updateAiBars = () => {
      const aiLevel = audioLevels.ai || 0;
      // // console.log('[AudioVisualizer updateAiBars] aiLevel:', aiLevel); // Disabled, too noisy
      setAiBars(createWaveform(aiLevel));
    };

    // Initial call to set bars without waiting for interval
    updateUserBars();
    updateAiBars();

    const userAnimationId = setInterval(updateUserBars, animationInterval);
    const aiAnimationId = setInterval(updateAiBars, animationInterval);

    return () => {
      clearInterval(userAnimationId);
      clearInterval(aiAnimationId);
    };
  }, [audioLevels, connectionState]); // Removed barCount from deps as it's constant here
  
  return (
    <S.AudioVisualizerContainer>
      <S.SpeakerLabel>You</S.SpeakerLabel>
      <S.VisualizerBars>
        {userBars.map((height, index) => (
          <S.VisualizerBar 
            key={`user-${index}`} 
            height={height} 
            speaker="user"
          />
        ))}
      </S.VisualizerBars>
      
      <S.SpeakerLabel>AI</S.SpeakerLabel>
      <S.VisualizerBars>
        {aiBars.map((height, index) => (
          <S.VisualizerBar 
            key={`ai-${index}`} 
            height={height} 
            speaker="ai"
          />
        ))}
      </S.VisualizerBars>
    </S.AudioVisualizerContainer>
  );
};

export default AudioVisualizer; 